import type { ResultOf } from '@graphql-typed-document-node/core'
import { atom, useAtomValue } from 'jotai'
import { atomWithQuery } from 'jotai-tanstack-query'
import { RestaurantSearchCourse } from '~/components/course/RestaurantSearchCourse.frag'
import type {
  ChildUse,
  FilterCriteria,
  FreeFlows,
  SeatAttributes as SeatAttributesType,
  SmokingAllowed,
} from '~/components/filterChips'
import {
  PlanAttributes,
  SeatAttributesFragment,
  createFilterCriteria,
  filterPlanByCriteria,
  filterSeatByCriteria,
  filterSeatBySeatType,
  hasAfternoonTea,
  hasBuffet,
  hasChildUses,
  hasFreeFlow,
  hasSeatAttributes,
  hasSmokingAllowed,
  showFilterChips,
  useBaseSetAfternoonTea,
  useBaseSetFreeFlows,
} from '~/components/filterChips'
import { filterPlanByPriceRange, hasChristmas } from '~/components/filterChips/criteria'
import { useBaseSetChristmas } from '~/components/filterChips/hooks'
import { PlanSearchPrice } from '~/components/plan/PlanSearchPrice.frag'
import { PlanSelectionInfo } from '~/components/plan/PlanSelectionInfo.frag'
import type { PriceCriteria } from '~/features/search/criteria'
import { getFragmentData, graphql } from '~/lib/graphql'
import type { SearchCoursesQueryVariables } from '~/lib/graphql/graphql'
import type { RestaurantId } from '~/lib/graphql/scalars'
import { createScope } from '~/lib/jotai'
import type { GuideContext } from '~/navigation'
import { createQueryAtom, createQueryOptions } from '~/stores/jotaiQueryHelper'

export const SearchCourses = graphql(/* GraphQL */ `
  query SearchCourses($restaurantId: RestaurantId!, $searchInput: SearchInput!) {
    restaurant(restaurantId: $restaurantId) {
      restaurantId
      name
      ...PlanRestaurantAllowDiscountOnReservation
      searchSeatOnlyPlans(input: $searchInput) {
        edges {
          node {
            ...PlanSelectionInfo
            ...PlanAttributes
            seats {
              ...SeatAttributes
            }
            searchPrice(input: $searchInput) {
              hasInventory
              fixedPoints
            }
          }
        }
      }
      searchCourses(input: $searchInput) {
        edges {
          node {
            ...RestaurantSearchCourse
          }
        }
      }
    }
  }
`)

const DEBUG_PREFIX = 'CourseList'

export const scope = createScope(DEBUG_PREFIX)

const filterCriteria$ = scope.atom<FilterCriteria | undefined>(undefined, 'filterCriteria$')

export function useFilterCriteria() {
  return useAtomValue(filterCriteria$)
}

const hasSearchInput$ = atom((get) => {
  return hasValue(get(variables$).searchInput)
})

export function useHasSearchInput() {
  return useAtomValue(hasSearchInput$)
}

// 絞り込み条件の有無で文言を調整したい
const hasCriteria$ = atom((get) => {
  const searchInput = get(hasSearchInput$)
  if (searchInput) {
    return true
  }
  const filter = get(filterCriteria$)
  return hasValue(filter) || hasValue(filter?.seatTypes) || hasValue(filter?.childUses)
})

function hasValue(v: Object | undefined) {
  return v !== undefined && Object.values(v).some((v) => v !== undefined)
}

export function useHasCriteria() {
  return useAtomValue(hasCriteria$)
}

export const setCriteria$ = atom(null, (_get, set, v?: GuideContext) => {
  if (v?.restaurantId === undefined) {
    return
  }
  set(filterCriteria$, createFilterCriteria(v))
})
setCriteria$.debugLabel = `${DEBUG_PREFIX}-setCriteria$`

export const variables$ = scope.atom<SearchCoursesQueryVariables>(
  {
    restaurantId: '' as RestaurantId,
    searchInput: {},
  },
  'variables$'
)

export const setVariables$ = atom(null, (_, set, v?: GuideContext) => {
  if (v?.restaurantId === undefined) {
    return
  }
  set(variables$, {
    restaurantId: v.restaurantId,
    searchInput: {
      visitDate: v?.visitDate,
      visitTime: v?.visitTime,
      visitorsCount: v?.visitorsCount,
    },
  })
})
variables$.debugLabel = `${DEBUG_PREFIX}-variables$`

const query$ = createQueryAtom<typeof SearchCourses>()
query$.debugLabel = `${DEBUG_PREFIX}-query$`

export const courses$ = atomWithQuery((get) =>
  createQueryOptions(SearchCourses, get(variables$), get(query$).fn, {
    enabled: get(variables$).restaurantId !== '',
    staleTime: 10 * 60 * 1000,
  })
)
courses$.debugLabel = `${DEBUG_PREFIX}-courses$`

// 取得したコースリストからプランをフラットに取り出すための atom
// 絞り込み条件を抽出するために使用するのみで、表示用には使わない
const allPlans$ = atom<ResultOf<typeof PlanAttributes>[]>((get) => {
  return (
    get(courses$)
      .data?.restaurant?.searchCourses.edges.flatMap(
        (edge) => getFragmentData(RestaurantSearchCourse, edge.node).searchPlans.edges
      )
      .flatMap((edge) => getFragmentData(PlanAttributes, edge.node)) ?? []
  )
})
allPlans$.debugLabel = `${DEBUG_PREFIX}-allPlans$`

// 取得したコースリストから席をフラットに取り出すための atom
// 絞り込み条件を抽出するために使用するのみで、表示用には使わない
const allSeats$ = atom<ResultOf<typeof SeatAttributesFragment>[]>((get) => {
  return (
    get(courses$)
      .data?.restaurant?.searchCourses.edges.flatMap(
        (edge) => getFragmentData(RestaurantSearchCourse, edge.node).searchPlans.edges
      )
      .flatMap((edge) => getFragmentData(SeatAttributesFragment, edge.seats)) ?? []
  )
})
allSeats$.debugLabel = `${DEBUG_PREFIX}-allSeats$`

const freeFlows$ = atom<FreeFlows>((get) => {
  return hasFreeFlow(get(allPlans$), get(filterCriteria$))
})
freeFlows$.debugLabel = `${DEBUG_PREFIX}-freeFlows$`

export function useFreeFlows() {
  return useAtomValue(freeFlows$)
}

const afternoonTea$ = atom<boolean>((get) => {
  return hasAfternoonTea(get(allPlans$), get(filterCriteria$))
})
afternoonTea$.debugLabel = `${DEBUG_PREFIX}-afternoonTea$`

export function useAfternoonTea() {
  return useAtomValue(afternoonTea$)
}

const christmas$ = atom<boolean>((get) => {
  return hasChristmas(get(allPlans$), get(filterCriteria$))
})
christmas$.debugLabel = `${DEBUG_PREFIX}-christmas$`

export function useChristmas() {
  return useAtomValue(christmas$)
}

const buffet$ = atom<boolean>((get) => {
  return hasBuffet(get(allPlans$), get(filterCriteria$))
})
buffet$.debugLabel = `${DEBUG_PREFIX}-buffet$`

export function useBuffet() {
  return useAtomValue(buffet$)
}

const smokingAllowed$ = atom<SmokingAllowed>((get) => {
  return hasSmokingAllowed(get(allSeats$), get(filterCriteria$))
})
smokingAllowed$.debugLabel = `${DEBUG_PREFIX}-smokingAllowed$`

export function useSmokingAllowed() {
  return useAtomValue(smokingAllowed$)
}

const seatAttributes$ = atom<SeatAttributesType>((get) => {
  return hasSeatAttributes(get(allSeats$), get(filterCriteria$))
})
seatAttributes$.debugLabel = `${DEBUG_PREFIX}-seatAttributes$`

export function useSeatAttributes() {
  return useAtomValue(seatAttributes$)
}

const childUses$ = atom<ChildUse>((get) => {
  return hasChildUses(get(allSeats$), get(filterCriteria$))
})
childUses$.debugLabel = `${DEBUG_PREFIX}-childUses$`

export function useChildUse() {
  return useAtomValue(childUses$)
}

const show$ = atom((get) => {
  const buffet = get(buffet$)
  const freeFlows = get(freeFlows$)
  const afternoonTea = get(afternoonTea$)
  const smokingAllowed = get(smokingAllowed$)
  const seatAttributes = get(seatAttributes$)
  const childUses = get(childUses$)
  return showFilterChips({
    buffet,
    freeFlows,
    afternoonTea,
    smokingAllowed,
    seatAttributes,
    childUses,
  })
})
show$.debugLabel = `${DEBUG_PREFIX}-show$`

export function useShow() {
  return useAtomValue(show$)
}

export const filteredCourses$ = atom((get) => {
  const result = get(courses$)
  if (result.data === undefined || result.data.restaurant === null) {
    return []
  }

  const criteria = get(filterCriteria$)
  // 絞り込み条件が指定されていない場合は、バックエンドから取得したコースをそのまま表示する
  if (criteria === undefined) {
    return result.data.restaurant.searchCourses.edges.map((edge) =>
      getFragmentData(RestaurantSearchCourse, edge.node)
    )
  }

  return result.data.restaurant.searchCourses.edges.map((edge) => {
    const course = getFragmentData(RestaurantSearchCourse, edge.node)

    // プランもしくは席の絞り込み条件が指定されている場合は、条件に合致するプランのみを表示する
    const plans = course.searchPlans.edges.filter(
      (e) =>
        filterPlanByCriteria(getFragmentData(PlanAttributes, e.node), criteria) &&
        filterPlanByPriceRange(
          getFragmentData(PlanSearchPrice, e.node.searchPrice)?.bestPrice,
          criteria
        ) &&
        e.seats.some(
          (s) =>
            filterSeatByCriteria(getFragmentData(SeatAttributesFragment, s), criteria) &&
            filterSeatBySeatType(getFragmentData(SeatAttributesFragment, s), criteria)
        )
    )

    return {
      ...course,
      searchPlans: { edges: plans },
    } as ResultOf<typeof RestaurantSearchCourse>
  })
})
filteredCourses$.debugLabel = `${DEBUG_PREFIX}-filteredCourses$`

const allPrices$ = atom<number[]>((get) => {
  return (
    get(courses$)
      .data?.restaurant?.searchCourses.edges.flatMap(
        (edge) => getFragmentData(RestaurantSearchCourse, edge.node).searchPlans.edges
      )
      .filter((edge) => getFragmentData(PlanSelectionInfo, edge.node).mealPeriod !== 'BREAKFAST')
      .flatMap((edge) => getFragmentData(PlanSearchPrice, edge.node.searchPrice)?.bestPrice ?? 0) ??
    []
  )
})
allPrices$.debugLabel = `${DEBUG_PREFIX}-allPrices$`

const initialPriceRange$ = atom<PriceCriteria>((get) => {
  const prices = get(allPrices$)
  return {
    lowerBoundPrice: Math.min(...prices),
    upperBoundPrice: Math.max(...prices),
  }
})
initialPriceRange$.debugLabel = `${DEBUG_PREFIX}-initialPriceRange$`

export function useInitialPriceRange() {
  return useAtomValue(initialPriceRange$)
}

const seatOnlyPlans$ = atom((get) => {
  return (
    get(courses$).data?.restaurant?.searchSeatOnlyPlans.edges.flatMap((e) => {
      if (!e.node.searchPrice?.hasInventory) {
        return []
      }
      return [
        {
          ...getFragmentData(PlanSelectionInfo, e.node),
          fixedPoints: e.node.searchPrice?.fixedPoints ?? 0,
        },
      ]
    }) ?? []
  )
})
seatOnlyPlans$.debugLabel = `${DEBUG_PREFIX}-seatOnlyPlans$`

const filteredSeatOnlyPlans$ = atom((get) => {
  const criteria = get(filterCriteria$)
  if (criteria === undefined) {
    // 絞り込み条件が指定されていない場合は、バックエンドから取得したコースをそのまま表示する
    return get(seatOnlyPlans$)
  }

  return (
    get(courses$)
      .data?.restaurant?.searchSeatOnlyPlans.edges.filter(
        // FIXME: この部分は共通化できそう
        (e) =>
          filterPlanByCriteria(getFragmentData(PlanAttributes, e.node), criteria) &&
          e.node.seats.some(
            (s) =>
              filterSeatByCriteria(getFragmentData(SeatAttributesFragment, s), criteria) &&
              filterSeatBySeatType(getFragmentData(SeatAttributesFragment, s), criteria)
          )
      )
      .flatMap((e) => {
        if (!e.node.searchPrice?.hasInventory) {
          return []
        }
        return [
          {
            ...getFragmentData(PlanSelectionInfo, e.node),
            fixedPoints: e.node.searchPrice?.fixedPoints ?? 0,
          },
        ]
      }) ?? []
  )
})
filteredSeatOnlyPlans$.debugLabel = `${DEBUG_PREFIX}-filteredSeatOnlyPlans$`

export function useSeatOnlyPlans() {
  return useAtomValue(filteredSeatOnlyPlans$)
}

export function useFilteredCourses() {
  return useAtomValue(filteredCourses$)
}
export function useSetFreeFlows() {
  return useBaseSetFreeFlows('GUIDE')
}

export function useSetAfternoonTea() {
  return useBaseSetAfternoonTea('GUIDE')
}

export function useSetChristmas() {
  return useBaseSetChristmas('GUIDE')
}

const filteredAllPlans$ = atom((get) => {
  return get(filteredCourses$).flatMap((course) => {
    return course.searchPlans.edges.map((edge) => getFragmentData(PlanAttributes, edge.node))
  })
})
filteredAllPlans$.debugLabel = `${DEBUG_PREFIX}-filteredAllPlans$`

const filteredAllSeats$ = atom((get) => {
  return get(filteredCourses$).flatMap((course) =>
    course.searchPlans.edges.flatMap((edge) => getFragmentData(SeatAttributesFragment, edge.seats))
  )
})
filteredAllSeats$.debugLabel = `${DEBUG_PREFIX}-filteredAllSeats$`

const enabledFreeFlows$ = atom((get) => {
  return hasFreeFlow(get(filteredAllPlans$), get(filterCriteria$))
})
enabledFreeFlows$.debugLabel = `${DEBUG_PREFIX}-enabledFreeFlows$`

export function useEnabledFreeFlows() {
  return useAtomValue(enabledFreeFlows$)
}

const enabledAfternoonTea$ = atom<boolean>((get) => {
  return hasAfternoonTea(get(filteredAllPlans$), get(filterCriteria$))
})
enabledAfternoonTea$.debugLabel = `${DEBUG_PREFIX}-enabledAfternoonTea$`

export function useEnabledAfternoonTea() {
  return useAtomValue(enabledAfternoonTea$)
}

const enabledChristmas$ = atom<boolean>((get) => {
  return hasChristmas(get(filteredAllPlans$), get(filterCriteria$))
})
enabledChristmas$.debugLabel = `${DEBUG_PREFIX}-enabledChristmas$`

export function useEnabledChristmas() {
  return useAtomValue(enabledChristmas$)
}

const enabledBuffet$ = atom<boolean>((get) => {
  return hasBuffet(get(filteredAllPlans$), get(filterCriteria$))
})
enabledBuffet$.debugLabel = `${DEBUG_PREFIX}-enabledBuffet$`

export function useEnabledBuffet() {
  return useAtomValue(enabledBuffet$)
}

const enabledSmokingAllowed$ = atom<SmokingAllowed>((get) => {
  return hasSmokingAllowed(get(filteredAllSeats$), get(filterCriteria$))
})
enabledSmokingAllowed$.debugLabel = `${DEBUG_PREFIX}-enabledSmokingAllowed$`

export function useEnabledSmokingAllowed() {
  return useAtomValue(enabledSmokingAllowed$)
}

const enabledSeatAttributes$ = atom<SeatAttributesType>((get) => {
  return hasSeatAttributes(get(filteredAllSeats$), get(filterCriteria$))
})
enabledSeatAttributes$.debugLabel = `${DEBUG_PREFIX}-enabledSeatAttributes$`

export function useEnabledSeatAttributes() {
  return useAtomValue(enabledSeatAttributes$)
}

const enabledChildUses$ = atom<ChildUse>((get) => {
  return hasChildUses(get(filteredAllSeats$), get(filterCriteria$))
})
enabledChildUses$.debugLabel = `${DEBUG_PREFIX}-enabledChildUses$`

export function useEnabledChildUses() {
  return useAtomValue(enabledChildUses$)
}

const seatOnlyAllPlans$ = atom((get) => {
  return (
    get(courses$).data?.restaurant?.searchSeatOnlyPlans.edges.flatMap((e) =>
      getFragmentData(PlanAttributes, e.node)
    ) ?? []
  )
})
seatOnlyAllPlans$.debugLabel = `${DEBUG_PREFIX}-seatOnlyAllPlans$`

const seatOnlyAllSeats$ = atom((get) => {
  return (
    get(courses$).data?.restaurant?.searchSeatOnlyPlans.edges.flatMap((e) =>
      getFragmentData(SeatAttributesFragment, e.node.seats)
    ) ?? []
  )
})
seatOnlyAllSeats$.debugLabel = `${DEBUG_PREFIX}-seatOnlyAllSeats$`

const seatOnlyFreeFlows$ = atom<FreeFlows>((get) => {
  return hasFreeFlow(get(seatOnlyAllPlans$), get(filterCriteria$))
})
seatOnlyFreeFlows$.debugLabel = `${DEBUG_PREFIX}-seatOnlyFreeFlows$`

export function useSeatOnlyFreeFlows() {
  return useAtomValue(seatOnlyFreeFlows$)
}

const seatOnlyAfternoonTea$ = atom<boolean>((get) => {
  return hasAfternoonTea(get(seatOnlyAllPlans$), get(filterCriteria$))
})
seatOnlyAfternoonTea$.debugLabel = `${DEBUG_PREFIX}-seatOnlyAfternoonTea$`

export function useSeatOnlyAfternoonTea() {
  return useAtomValue(seatOnlyAfternoonTea$)
}

const seatOnlyBuffet$ = atom<boolean>((get) => {
  return hasBuffet(get(seatOnlyAllPlans$), get(filterCriteria$))
})
seatOnlyBuffet$.debugLabel = `${DEBUG_PREFIX}-seatOnlyBuffet$`

export function useSeatOnlyBuffet() {
  return useAtomValue(seatOnlyBuffet$)
}

const seatOnlySmokingAllowed$ = atom<SmokingAllowed>((get) => {
  return hasSmokingAllowed(get(seatOnlyAllSeats$), get(filterCriteria$))
})
seatOnlySmokingAllowed$.debugLabel = `${DEBUG_PREFIX}-seatOnlySmokingAllowed$`

export function useSeatOnlySmokingAllowed() {
  return useAtomValue(seatOnlySmokingAllowed$)
}

const seatOnlySeatAttributes$ = atom<SeatAttributesType>((get) => {
  return hasSeatAttributes(get(seatOnlyAllSeats$), get(filterCriteria$))
})
seatOnlySeatAttributes$.debugLabel = `${DEBUG_PREFIX}-seatOnlySeatAttributes$`

export function useSeatOnlySeatAttributes() {
  return useAtomValue(seatOnlySeatAttributes$)
}

const seatOnlyChildUses$ = atom<ChildUse>((get) => {
  return hasChildUses(get(seatOnlyAllSeats$), get(filterCriteria$))
})
seatOnlyChildUses$.debugLabel = `${DEBUG_PREFIX}-seatOnlyChildUses$`

export function useSeatOnlyChildUses() {
  return useAtomValue(seatOnlyChildUses$)
}

const showSeatOnly$ = atom((get) => {
  const buffet = get(seatOnlyBuffet$)
  const freeFlows = get(seatOnlyFreeFlows$)
  const afternoonTea = get(seatOnlyAfternoonTea$)
  const smokingAllowed = get(seatOnlySmokingAllowed$)
  const seatAttributes = get(seatOnlySeatAttributes$)
  const childUses = get(seatOnlyChildUses$)
  return showFilterChips({
    buffet,
    freeFlows,
    afternoonTea,
    smokingAllowed,
    seatAttributes,
    childUses,
  })
})
show$.debugLabel = `${DEBUG_PREFIX}-show$`

export function useShowSeatOnly() {
  return useAtomValue(showSeatOnly$)
}
